"use client";

import React, { useContext, useEffect, useMemo, useRef } from "react";
import Fab from "@mui/material/Fab";
import {
  Box,
  Grow,
  Stack,
  ClickAwayListener,
  useMediaQuery,
  alpha,
  useTheme,
  Typography,
  IconButton,
} from '@mui/material';
import Chat from './Chat';
import { ChatContext } from './context';
import { useWidgetSettingsContext } from './settings';
import chroma from 'chroma-js';
import Iconify from './components/iconify';
import Image from './components/image';
import { QRCodeCanvas } from 'qrcode.react';

export default function WidgetChat(): JSX.Element {
  const { chatOpen, setChatOpen, editMode, isHome } = useContext(ChatContext);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const elementRef = useRef(null);
  const theme = useTheme();
  const settings = useWidgetSettingsContext();

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.data === "close-chat-widget") {
        setChatOpen(false);
      }
    };
    window.addEventListener("message", listener);

    return () => {
      window.removeEventListener("message", listener);
    };
  }, []);

  useEffect(() => {
    if (chatOpen) {
      window.postMessage("open-chat-widget", "*");
    }
  }, [chatOpen]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      [data-simplebar]{position:relative;flex-direction:column;flex-wrap:wrap;justify-content:flex-start;align-content:flex-start;align-items:flex-start}.simplebar-wrapper{overflow:hidden;width:inherit;height:inherit;max-width:inherit;max-height:inherit}.simplebar-mask{direction:inherit;position:absolute;overflow:hidden;padding:0;margin:0;left:0;top:0;bottom:0;right:0;width:auto!important;height:auto!important;z-index:0}.simplebar-offset{direction:inherit!important;box-sizing:inherit!important;resize:none!important;position:absolute;top:0;left:0;bottom:0;right:0;padding:0;margin:0;-webkit-overflow-scrolling:touch}.simplebar-content-wrapper{direction:inherit;box-sizing:border-box!important;position:relative;display:block;height:100%;width:auto;max-width:100%;max-height:100%;overflow:auto;scrollbar-width:none;-ms-overflow-style:none}.simplebar-content-wrapper::-webkit-scrollbar,.simplebar-hide-scrollbar::-webkit-scrollbar{display:none;width:0;height:0}.simplebar-content:after,.simplebar-content:before{content:' ';display:table}.simplebar-placeholder{max-height:100%;max-width:100%;width:100%;pointer-events:none}.simplebar-height-auto-observer-wrapper{box-sizing:inherit!important;height:100%;width:100%;max-width:1px;position:relative;float:left;max-height:1px;overflow:hidden;z-index:-1;padding:0;margin:0;pointer-events:none;flex-grow:inherit;flex-shrink:0;flex-basis:0}.simplebar-height-auto-observer{box-sizing:inherit;display:block;opacity:0;position:absolute;top:0;left:0;height:1000%;width:1000%;min-height:1px;min-width:1px;overflow:hidden;pointer-events:none;z-index:-1}.simplebar-track{z-index:1;position:absolute;right:0;bottom:0;pointer-events:none;overflow:hidden}[data-simplebar].simplebar-dragging{pointer-events:none;-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}[data-simplebar].simplebar-dragging .simplebar-content{pointer-events:none;-webkit-touch-callout:none;-webkit-user-select:none;-khtml-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}[data-simplebar].simplebar-dragging .simplebar-track{pointer-events:all}.simplebar-scrollbar{position:absolute;left:0;right:0;min-height:10px}.simplebar-scrollbar:before{position:absolute;content:'';background:#000;border-radius:7px;left:2px;right:2px;opacity:0;transition:opacity .2s .5s linear}.simplebar-scrollbar.simplebar-visible:before{opacity:.5;transition-delay:0s;transition-duration:0s}.simplebar-track.simplebar-vertical{top:0;width:11px}.simplebar-scrollbar:before{top:2px;bottom:2px;left:2px;right:2px}.simplebar-track.simplebar-horizontal{left:0;height:11px}.simplebar-track.simplebar-horizontal .simplebar-scrollbar{right:auto;left:0;top:0;bottom:0;min-height:0;min-width:10px;width:auto}[data-simplebar-direction=rtl] .simplebar-track.simplebar-vertical{right:auto;left:0}.simplebar-dummy-scrollbar-size{direction:rtl;position:fixed;opacity:0;visibility:hidden;height:500px;width:500px;overflow-y:hidden;overflow-x:scroll;-ms-overflow-style:scrollbar!important}.simplebar-dummy-scrollbar-size>div{width:200%;height:200%;margin:10px 0}.simplebar-hide-scrollbar{position:fixed;left:0;visibility:hidden;overflow-y:scroll;scrollbar-width:none;-ms-overflow-style:none}
    `;
    ref?.current?.appendChild(style);

    const style2 = document.createElement('style');
    style2.innerHTML = `
    .ul-selectable-options {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .selectable-options-li {
      cursor: pointer;
      font-size: 14px;
      margin-bottom: 6px;
    }
    .selectable-options-li:hover {
      text-decoration: underline;
    }
  `;
    ref?.current?.appendChild(style2);
  }, [ref?.current]);

  const fabBoxStyle = useMemo(
    () => ({
      position: 'fixed',
      zIndex: 999,
      ...(settings.position === 'left'
        ? {
            left: settings.sideSpacing,
          }
        : {
            right: settings.sideSpacing,
          }),

      bottom: settings.bottomSpacing,
      '& .MuiFab-root': {
        backgroundColor: settings.noLauncherBg
          ? 'transparent'
          : settings.laucherBgColor!,
        boxShadow: settings.noLauncherBg ? 'none' : 'inherit',
        ' &:hover, &:active': {
          backgroundColor: settings.noLauncherBg
            ? 'transparent'
            : alpha(settings.laucherBgColor!, 0.8),
        },
      },
    }),
    [theme, settings],
  );

  const chatBoxStyle = useMemo(
    () => ({
      position: 'fixed',
      bottom: {
        xs: 0,
        sm: 76 + settings.bottomSpacing,
        md: 86 + settings.bottomSpacing,
      },

      ...(settings.position === 'left'
        ? {
            left: {
              xs: 0,
              sm: settings.sideSpacing,
            },
          }
        : {
            right: {
              xs: 0,
              sm: settings.sideSpacing,
            },
          }),

      minWidth: {
        sm: 300,
      },
      maxWidth: 600,
      width: {
        xs: 1,
        sm: 0.4,
        md: 0.3,
      },
    }),
    [settings],
  );

  const handleClickAway = () => {
    if (!editMode && !isHome) setChatOpen(false);
  };

  useEffect(() => {
    // When the widget is opened
    if (chatOpen) {
      window.parent.postMessage(
        { widgetState: 'open', height: '700px', width: '350px' },
        '*',
      );
      if (window.innerWidth < 600) {
        document.documentElement.style.cssText = 'overflow: hidden !important';
      }
    } else {
      window.parent.postMessage(
        { widgetState: 'closed', height: '56px', width: '56px' },
        '*',
      );
      document.documentElement.style.cssText = 'overflow: auto !important';
    }

    // When the widget is closed
  }, [chatOpen]);

  const WhatsAppIconLaunch = () => {
    return (
      <IconButton
        onClick={() => {
          const message = encodeURIComponent('/start');
          window.open(
            'https://wa.me/' +
              settings.whatsappNumber?.slice(1) +
              (settings?.prefillMessageToStartAssistant
                ? '?text=' + message
                : ''),
            '_blank',
          );
        }}
        sx={{
          position: 'absolute',
          top: -(chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68) - 8,
          left: 0,
          zIndex: 9999,
          width: chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68,
          height: chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68,
        }}
      >
        <Iconify
          width={chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68}
          height={chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68}
          icon="logos:whatsapp-icon"
        />
      </IconButton>
    );
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Box ref={ref} />
        <Stack
          id="chat-widget-container"
          sx={{ zIndex: chatOpen ? 1200 : -1, ...chatBoxStyle }}
        >
          <Grow
            in={chatOpen}
            style={{
              transformOrigin: 'bottom right',
            }}
          >
            <Box sx={{ width: 1 }}>
              <Chat />
            </Box>
          </Grow>
        </Stack>
        <Box id="chat-widget-fab" sx={fabBoxStyle} ref={elementRef}>
          {!chatOpen && settings.whatsappNumber && <WhatsAppIconLaunch />}

          {isMobile && !chatOpen && (
            <Grow in style={{ transformOrigin: 'top left' }}>
              <Fab
                onClick={() => {
                  setChatOpen((prev) => !prev);
                }}
                sx={{
                  p: 1,
                  width: settings?.launcherIconSizeMobile || 68,
                  height: settings?.launcherIconSizeMobile || 68,
                }}
              >
                {settings.welcomeMessage && (
                  <Typography
                    className="welcome-text"
                    variant="subtitle1"
                    color={
                      settings.welcomeMessageColor
                        ? settings.welcomeMessageColor
                        : chroma(
                            settings.laucherBgColor || 'white',
                          ).luminance() > 0.5
                        ? 'text.primary'
                        : 'text.secondary'
                    }
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(settings.laucherBgColor!, 0.8),
                      },
                      transition: (theme) => theme.transitions.create('all'),
                      position: 'absolute',
                      ...(settings.position === 'left'
                        ? { right: -8 }
                        : { left: -8 }),
                      top: '50%',
                      transform: 'translate(-100%, -50%)',
                      backgroundColor: settings.laucherBgColor!,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                    }}
                    noWrap
                  >
                    {settings.welcomeMessage}
                  </Typography>
                )}
                {chatOpen ? (
                  <Iconify
                    icon={'material-symbols:close-rounded'}
                    width={40}
                    height={40}
                    sx={{
                      color:
                        chroma(settings.laucherBgColor || 'white').luminance() >
                        0.5
                          ? '#000'
                          : 'white',
                    }}
                  />
                ) : (settings?.launcherIcon as any)?.preview ? (
                  <Image
                    sx={{
                      borderRadius: '100%',
                      aspectRatio: 1,
                      width: 1,
                    }}
                    src={(settings?.launcherIcon as any)?.preview}
                  />
                ) : (
                  <Iconify
                    icon={'ic:round-chat'}
                    sx={{
                      color:
                        chroma(settings.laucherBgColor || 'white').luminance() >
                        0.5
                          ? '#000'
                          : '#fff',
                    }}
                  />
                )}
              </Fab>
            </Grow>
          )}

          {!isMobile && (
            <Fab
              onClick={() => {
                setChatOpen((prev) => !prev);
              }}
              sx={{
                p: 1,
                width: chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68,
                height: chatOpen ? 40 : settings?.launcherIconSizeDesktop || 68,
              }}
            >
              {settings.welcomeMessage && (
                <Typography
                  className="welcome-text"
                  variant="subtitle1"
                  color={
                    settings.welcomeMessageColor
                      ? settings.welcomeMessageColor
                      : chroma(settings.laucherBgColor || 'white').luminance() >
                        0.5
                      ? 'text.primary'
                      : 'text.secondary'
                  }
                  sx={{
                    '&:hover': {
                      backgroundColor: alpha(settings.laucherBgColor!, 0.8),
                    },
                    transition: (theme) => theme.transitions.create('all'),
                    position: 'absolute',
                    ...(settings.position === 'right'
                      ? { left: -8, transform: 'translate(-100%, -50%)' }
                      : { right: -8, transform: 'translate(100%, -50%)' }),
                    top: '50%',
                    backgroundColor: settings.laucherBgColor!,
                    py: 0.5,
                    px: 1,
                    borderRadius: 1,
                  }}
                  noWrap
                >
                  {settings.welcomeMessage}
                </Typography>
              )}
              {chatOpen ? (
                <Iconify
                  icon={'material-symbols:close-rounded'}
                  width={40}
                  height={40}
                  sx={{
                    color:
                      chroma(settings.laucherBgColor || 'white').luminance() >
                      0.5
                        ? '#000'
                        : '#fff',
                  }}
                />
              ) : (settings?.launcherIcon as any)?.preview ? (
                <Image
                  sx={{
                    borderRadius: '100%',
                    aspectRatio: 1,
                    width: 1,
                  }}
                  src={(settings?.launcherIcon as any)?.preview}
                />
              ) : (
                <Iconify
                  icon={'ic:round-chat'}
                  sx={{
                    color:
                      chroma(settings.laucherBgColor || 'white').luminance() >
                      0.5
                        ? '#000'
                        : '#fff',
                  }}
                />
              )}
            </Fab>
          )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
